<template>
  <div class="option__header">
    <h3 class="option__title">
      <span class="title__badge">{{ groupName }}</span>
    </h3>
    <span class="header__text">
      {{ countText }}
    </span>
  </div>
  <ul class="menu-option__ul list-style-none" :style="gridStyle">
    <li v-for="(option, index) in options" :key="index">
      <MenuOptionItem
        :menu-type="menuType"
        :option="option"
        :selected-options="selectedOptions"
        :price="getAddPrice(option)"
        @on-click="handleClick(option)"
      />
    </li>
  </ul>
</template>

<script setup lang="ts">
  import MenuOptionItem from '@/components/MenuOptionItem.vue';
  import CartMenuOption from '@/interfaces/CartMenuOption';
  import SetMenu from '@/interfaces/SetMenu';
  import Topping from '@/interfaces/Topping';
  import { useMenuStore } from '@/stores/menu';
  import { useModalStore } from '@/stores/modal';
  import { computed, onMounted, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import MenuOptionCounter from './MenuOptionCounter.vue';

  interface Props {
    /** 메뉴 아이템 코드 */
    menuItemCode: string;
    /** 메뉴 타입 */
    menuType: 'M' | 'S';
    /** 메뉴 세금2 사용 여부 */
    menuTax2Used: boolean;
    /** 옵션 그룹 코드 */
    groupCode: string | number;
    /** 옵션 그룹 이름 */
    groupName: string;
    /** 최소 선택 가능 수량 */
    minCount: number;
    /** 최대 선택 가능 수량 */
    maxCount: number;
    /** 한 줄에 표시할 옵션 개수 */
    columnCount?: number;
  }

  const props = withDefaults(defineProps<Props>(), {
    columnCount: 2,
  });
  const emits = defineEmits(['change']);

  const { t } = useI18n();
  const menuStore = useMenuStore();
  const modalStore = useModalStore();

  const selectedOptions = ref<CartMenuOption[]>([]);

  // 옵션이 변경되는 경우 부모 컴포넌트로 이벤트 전달
  watch(selectedOptions.value, () => {
    emits('change', selectedOptions.value);
  });

  /**
   * 토핑 또는 세트메뉴 리스트
   * @param groupCode 그룹 코드
   * @returns 토핑 리스트
   */
  const options = computed(() => {
    if (props.menuType === 'M') {
      return menuStore.toppings.filter((topping) => topping.GroupCode === props.groupCode);
    } else if (props.menuType === 'S') {
      return menuStore.setMenus.filter(
        (set) => set.ItemCode === props.menuItemCode && set.GroupCode === props.groupCode,
      );
    }
    return [];
  });

  // 그룹내 선택 가능한 수량
  const groupSelectableQty = computed(() => {
    return props.maxCount - selectedOptions.value.reduce((acc, option) => acc + option.Qty, 0);
  });

  const gridStyle = computed(() => {
    return { gridTemplateColumns: `repeat(${props.columnCount}, 1fr)` };
  });

  onMounted(() => {
    // 옵션 자동 선택 적용
    setAutomaticSelection();
  });

  /** 옵션 선택 */
  const handleClick = async (option: Topping | SetMenu) => {
    let qty = 1;

    // 품절인 경우
    if (option.IsSoldOut) return;

    // 현재 옵션 인덱스 조회
    const findIndex = selectedOptions.value.findIndex(
      (item) =>
        item.OptionCode ===
        (props.menuType === 'M' ? (option as Topping).ToppingCode : (option as SetMenu).SetMenuCode),
    );

    // 옵션 선택 or 해제 분기
    if (findIndex === -1) {
      // 최대 개수가 1이면 이전 선택을 삭제
      if (props.maxCount === 1) {
        selectedOptions.value.splice(0, 1);
      }

      // 선택 가능한 수량이 0이면 종료
      if (props.maxCount > 0 && groupSelectableQty.value === 0) return;

      // 최대 개수보다 많이 선택 금지
      if (props.maxCount > 0 && selectedOptions.value.length >= props.maxCount) return;

      // 토핑 옵션이면서 최대 수량이 1보다 크게 설정되어 있다면 수량 입력 모달 열기
      const selectableQty = props.maxCount === 0 ? (option as Topping).MaxCount : groupSelectableQty.value;
      if (props.menuType === 'M' && (option as Topping).MaxCount > 1 && selectableQty > 1) {
        qty = await modalStore.openSyncModalAsync<typeof MenuOptionCounter, number>(MenuOptionCounter, {
          groupName: props.groupName,
          groupSelectableQty: selectableQty,
          topping: option as Topping,
        });
        if (qty === 0) return;
      }

      // 옵션 추가
      selectedOptions.value.push(convertOptionToCartOption(option, qty));
    } else {
      // 최소, 최대 개수가 1이면 해제 금지
      if (props.minCount === 1 && props.maxCount === 1) return;

      // 옵션 해제
      selectedOptions.value.splice(findIndex, 1);
    }
  };

  /** 장바구니 옵션 데이터 생성 */
  const convertOptionToCartOption = (option: Topping | SetMenu, qty: number) => {
    const cartMenuOption: CartMenuOption = {
      MenuItemCode: props.menuItemCode,
      GroupCode: option.GroupCode,
      OptionCode: props.menuType === 'M' ? (option as Topping).ToppingCode : (option as SetMenu).SetMenuCode,
      Qty: qty,
    };

    return cartMenuOption;
  };

  /**
   * 토핑 그룹별 최소, 최대 개수 안내 텍스트 조회
   * @param toppingGroup 토핑 그룹 데이터
   */
  const countText = computed(() => {
    if (props.minCount === 0 && props.maxCount === 0) return '';

    if (props.minCount === props.maxCount) {
      return t('label.option_static', { count: props.minCount });
    }

    if (props.minCount > 0 && props.maxCount > 0) {
      return t('label.option_min_max', { min: props.minCount, max: props.maxCount });
    }

    if (props.minCount > 0 && props.maxCount === 0) {
      return t('label.option_min', { min: props.minCount });
    }

    if (props.minCount === 0 && props.maxCount > 0) {
      return t('label.option_max', { max: props.maxCount });
    }

    return '';
  });

  /** 추가 금액 조회 */
  const getAddPrice = (option: Topping | SetMenu) => {
    if (props.menuType === 'M') {
      return menuStore.getToppingPrice(option as Topping, props.menuTax2Used);
    } else if (props.menuType === 'S') {
      return menuStore.getSetMenuPrice(option as SetMenu, props.menuTax2Used);
    }

    return 0;
  };

  /** 자동 선택 설정 */
  const setAutomaticSelection = () => {
    // 자동 선택 체크
    if (props.menuType === 'M') {
      // 토핑의 경우 자동선택 옵션 체크
      for (const option of options.value) {
        if ((option as Topping).IsSelect && !option.IsSoldOut) {
          selectedOptions.value.push(convertOptionToCartOption(option, 1));
        }
      }
    } else if (props.menuType === 'S') {
      // 그룹내 옵션의 개수가 1개면 자동선택
      if (options.value.length === 1) {
        selectedOptions.value.push(convertOptionToCartOption(options.value[0], 1));
      }
    }
  };
</script>

<style lang="scss" scoped>
  @use '@/assets/scss/variables' as vars;

  .option__header {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    padding-bottom: 0.597vw;
    @media (max-width: vars.$sm) {
      padding-bottom: 12px;
    }
    .header__text {
      position: relative;
      font-size: 1.4925vw;
      font-weight: 700;
      @media (max-width: vars.$sm) {
        font-size: 12px;
      }
      &:after {
        position: absolute;
        bottom: 0.0746vw;
        right: 0;
        width: calc(100% - 0.8955vw);
        height: 0.0746vw;
        background: #000;
        content: '';
      }
    }
  }
  .option__title {
    display: flex;
    align-items: center;
    font-size: 1.2687vw;
    font-weight: 500;
    @media (max-width: vars.$sm) {
      font-size: 14px;
    }
    .title__badge {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 2.0896vw;
      border-radius: 1.0448vw;
      background: var(--brand-primary);
      font-size: 1.2687vw;
      font-weight: 500;
      color: #fff;
      padding: 0 0.7463vw;
      margin-right: 0.3731vw;
      @media (max-width: vars.$sm) {
        font-size: 12px;
        height: 20px;
        border-radius: 10px;
        padding: 0 8px;
        margin-right: 6px;
      }
    }
  }
  .menu-option__ul {
    display: grid;
    flex-wrap: wrap;
    gap: 0.7463vw;

    @media (max-width: vars.$sm) {
      gap: 2px;
      margin: 0;
    }
  }
</style>
